/**
 * Asteroid rendering. Each asteroid is a slightly irregular sphere.
 * AsteroidField uses a single useFrame to read positions directly from store,
 * bypassing React prop pipeline for zero-lag visual updates (same pattern as PlayerShip).
 *
 * Velocity-based sub-tick extrapolation smooths over:
 * - 20Hz asteroid sync corrections (snap to server positions)
 * - Variable frame timing between physics ticks
 * Position: visualPos = physicsPos + velocity * alpha * TICK_DELTA
 */

import { useRef, useMemo, useCallback } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { WorldSetup } from '@warmancer/shared';
import { TICK_DELTA, mulberry32 } from '@warmancer/shared';
import { useGameStore } from '../stores/gameStore';

const ASTEROID_COLOR = '#8a7565';
const ASTEROID_EMISSIVE = '#3a2f28';

interface AsteroidFieldProps {
  readonly setup: WorldSetup;
}

export function AsteroidField({ setup }: AsteroidFieldProps) {
  const meshRefs = useRef<(THREE.Mesh | null)[]>([]);
  const masterSeed = useGameStore((s) => s.scenerySetup?.masterSeed ?? 0);

  const setMeshRef = useCallback((index: number) => (el: THREE.Mesh | null) => {
    meshRefs.current[index] = el;
  }, []);

  // Velocity-based sub-tick extrapolation: snap visual positions to
  // physicsPos + velocity * alpha * TICK_DELTA each frame.
  useFrame(() => {
    const positions = useGameStore.getState().asteroidPositions;
    if (positions.length === 0) return;
    const alpha = useGameStore.getState().renderAlpha;

    for (let i = 0; i < positions.length; i++) {
      const mesh = meshRefs.current[i];
      if (!mesh) continue;

      const pos = positions[i];
      mesh.position.x = pos.x + pos.vx * alpha * TICK_DELTA;
      mesh.position.z = pos.y + pos.vy * alpha * TICK_DELTA;
    }
  });

  // Use worldSetup asteroids for initial render (count and radii are fixed)
  const asteroids = setup.asteroids;

  // Initialize refs array to correct length
  if (meshRefs.current.length !== asteroids.length) {
    meshRefs.current = new Array(asteroids.length).fill(null);
  }

  return (
    <group>
      {asteroids.map((a, i) => (
        <AsteroidMesh
          key={i}
          radius={a.radius}
          initialX={a.x}
          initialY={a.y}
          seed={(masterSeed ^ 0xA57E0000) + i}
          refCallback={setMeshRef(i)}
        />
      ))}
    </group>
  );
}

interface AsteroidMeshProps {
  readonly radius: number;
  readonly initialX: number;
  readonly initialY: number;
  readonly seed: number;
  readonly refCallback: (el: THREE.Mesh | null) => void;
}

function AsteroidMesh({ radius, initialX, initialY, seed, refCallback }: AsteroidMeshProps) {
  // Create slightly irregular geometry by displacing vertices (deterministic via seed)
  const geometry = useMemo(() => {
    const rng = mulberry32(seed);
    const geo = new THREE.IcosahedronGeometry(radius, 1);
    const positions = geo.attributes.position;
    for (let i = 0; i < positions.count; i++) {
      const px = positions.getX(i);
      const py = positions.getY(i);
      const pz = positions.getZ(i);
      const len = Math.sqrt(px * px + py * py + pz * pz);
      const variation = 0.85 + rng() * 0.3;
      positions.setXYZ(i, (px / len) * radius * variation, (py / len) * radius * variation, (pz / len) * radius * variation);
    }
    positions.needsUpdate = true;
    geo.computeVertexNormals();
    return geo;
  }, [radius, seed]);

  return (
    <mesh ref={refCallback} position={[initialX, 0, initialY]} geometry={geometry}>
      <meshStandardMaterial
        color={ASTEROID_COLOR}
        emissive={ASTEROID_EMISSIVE}
        emissiveIntensity={1.0}
        roughness={0.9}
        flatShading
      />
    </mesh>
  );
}
